import { DeleteModal } from "@/components/modal/DeleteModal";
import { RestoreAllModal } from "@/components/modal/RestoreAllModal";
import { TrashSelectionActionModal } from "@/components/modal/TrashSelectionActionModal";
import { UnlinkModal } from "@/components/modal/UnlinkModal";
import { MainSidebar } from "@/components/sidebars/MainSidebar";
import { BreadcrumbSkeleton, SlideCardSkeleton } from "@/components/skeletons";
import { SlideCard } from "@/components/slides/list/SlideCard";
import { SelectionProvider, useSelection } from "@/contexts/SelectionContext";
import { GetLessonNameDocument, SortOrder } from "@/generated/graphql";
import { ITEM_LIMIT } from "@/lib/constants/shared";
import { useSlidesByLessonId } from "@/lib/hooks/slides/useSlidesByLessonId";
import type { SlideFields } from "@/types/Slide";

import { Button, IconButton, LoadMoreButton } from "@ai-teach/ui-web";
import { useQuery } from "@apollo/client/react";
import { Link, useParams } from "@tanstack/react-router";
import { IoIosArrowRoundBack } from "react-icons/io";
import { MdDelete, MdRestore } from "react-icons/md";
import { RiDeleteBin6Fill } from "react-icons/ri";

function LessonTrashPageContent() {
  const {
    clearAllSelections,
    selectSlides,
    toggleRestoreModal,
    toggleDeleteModal,
    isRestoreModalOpen,
    isDeleteModalOpen,
    isSelectionMode,
    isUnlinkModalOpen,
  } = useSelection();

  const params = useParams({ from: "/admin/lesson/$lessonId/trash" });
  const lessonId = params.lessonId;

  const { data: lessonNameData, loading: lessonNameLoading } = useQuery(GetLessonNameDocument, {
    variables: { id: lessonId },
    fetchPolicy: "cache-first",
  });

  const {
    lessonSlideItems: trashedSlides,
    lessonSlidesLoading,
    handleLoadMoreLessonSlides,
    lessonSlidesHasMore,
  } = useSlidesByLessonId({
    lessonId,
    searchText: "",
    sortBy: SortOrder.Recent,
    isTrash: true,
  });

  // Only the slides already fetched are acted on; "all" means everything currently listed.
  const selectAllSlides = () => {
    clearAllSelections();
    selectSlides(
      trashedSlides.map((slide) => ({
        id: slide.id,
        isLinkedCopy: slide.isLinkedCopy ?? false,
      }))
    );
  };

  const handleRestoreAll = () => {
    selectAllSlides();
    toggleRestoreModal("Slides");
  };

  const handleDeleteAll = () => {
    selectAllSlides();
    toggleDeleteModal(true);
  };

  const lesson = lessonNameData?.lesson;

  if (!lesson && !lessonSlidesLoading) {
    return <div className="flex items-center justify-center h-screen">Lesson not found</div>;
  }

  return (
    <div className="flex h-screen">
      <MainSidebar />

      {/* Main content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-6">
          {/* Back Navigation */}
          <Link
            to="/admin/lesson/$lessonId"
            params={{ lessonId }}
            className="text-gray-500 flex items-center gap-1"
          >
            <IoIosArrowRoundBack fontSize={30} />{" "}
            {lessonNameLoading ? (
              <span className="inline-block h-5 w-32 bg-slate-200 animate-pulse rounded" />
            ) : (
              lesson?.name
            )}
          </Link>

          {/* Page Title */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="h3 flex-1 text-center">
              {lessonNameLoading ? (
                <span className="inline-block h-9 w-48 bg-slate-200 animate-pulse rounded" />
              ) : (
                `${lesson?.name || ""} Trash`
              )}
            </h1>
          </div>

          {/* Trashed Slides Section */}
          {lessonSlidesLoading && trashedSlides.length === 0 ? (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="h4">Trashed Slides</h2>
              </div>
              {/* Slides Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {Array.from({ length: ITEM_LIMIT.generalItemLimit }).map((_, i) => (
                  <SlideCardSkeleton key={`slide-skeleton-card-${i + 1}`} slideNumber={i + 1} />
                ))}
              </div>
            </>
          ) : trashedSlides.length === 0 && !lessonSlidesLoading ? (
            <div className="flex flex-col justify-center items-center min-h-[230px] gap-2">
              <div className="text-6xl text-gray-300">
                <RiDeleteBin6Fill />
              </div>
              <p className="text-xl font-medium card-title">Trash is empty</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="h4">Trashed Slides</h2>
                <div className="flex items-center gap-3">
                  {/* Restore All Button */}
                  <Button
                    type="button"
                    onClick={handleRestoreAll}
                    variant="primary"
                    className="group hover:scale-105"
                  >
                    <MdRestore className="text-xl group-hover:rotate-[-15deg] transition-transform duration-200" />
                    <span>Restore All</span>
                  </Button>
                  {/* Delete All Button */}
                  <Button
                    type="button"
                    onClick={handleDeleteAll}
                    variant="danger"
                    className="group hover:scale-105"
                  >
                    <MdDelete className="text-xl" />
                    <span>Delete All</span>
                  </Button>
                </div>
              </div>
              {/* Slides Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {trashedSlides.map((slide, index) => {
                  if (!slide) return null;
                  return (
                    <SlideCard
                      key={slide.id}
                      slide={slide as SlideFields}
                      slideNumber={index + 1}
                      url="trash"
                    />
                  );
                })}
              </div>
              <LoadMoreButton
                onClick={handleLoadMoreLessonSlides}
                hasMore={lessonSlidesHasMore}
                loading={lessonSlidesLoading}
              />
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {isRestoreModalOpen && (
        <RestoreAllModal isOpen={true} modalType={isRestoreModalOpen} sourceId={lessonId} />
      )}
      {isDeleteModalOpen && <DeleteModal isOpen={isDeleteModalOpen} />}
      {isSelectionMode && <TrashSelectionActionModal page="lessonTrashPage" />}
      {isUnlinkModalOpen && <UnlinkModal isOpen={true} />}
    </div>
  );
}

export function LessonTrashPage() {
  return (
    <SelectionProvider>
      <LessonTrashPageContent />
    </SelectionProvider>
  );
}
